import React, { useState, useRef } from 'react';
import { PricingRulesEditor } from './InventoryProductCard';
import { API_URL, ADMIN_TOKEN_KEY, uploadProductImage, PRODUCT_MATERIALS, PRODUCT_STYLES, FABRIC_MAP } from './shared';
import { detectDominantColor } from '../../utils/dominantColor';
import { SizeChipsEditor, ColorPhotosEditor, PhotoLibraryModal, SelectionsEditor, BadgesEditor } from './editProductParts';

export const EditProductModal = ({ product, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name_es: product.name?.es || '',
    name_en: product.name?.en || '',
    desc_es: product.description?.es || '',
    desc_en: product.description?.en || '',
    material: product.material || 'algodon',
    style: product.style || 'T-Shirt',
    price_mxn: product.price_mxn ?? '',
    kids_price_mxn: product.kids_price_mxn ?? '',
    is_new: !!product.is_new,
    no_tzitzit: !!product.no_tzitzit,
  });
  const [sizes, setSizes] = useState(product.sizes?.es || []);
  const [sizeInput, setSizeInput] = useState('');
  const [colors, setColors] = useState((product.colors || []).map((c, i) => ({
    uid: `col-${i}`,
    original: c,
    name: c.name?.es || '',
    hex: c.value || '#FFFFFF',
    existing: [c.image, ...(c.gallery || [])].filter(Boolean),
    files: [],
  })));

  const styleOpts = PRODUCT_STYLES.includes(form.style) ? PRODUCT_STYLES : [form.style, ...PRODUCT_STYLES];

  // ---- Selecciones (variantes builtin + personalizadas) ----
  const optState = (key, oosF, hidF) =>
    (product[hidF] || []).includes(key) ? 'hidden' : (product[oosF] || []).includes(key) ? 'oos' : 'visible';
  const showTraditions = !product.no_tzitzit && !product.is_promo;
  // Products whose storefront shows a single "Incluidos" thread chip (renombrable)
  const HILOS_INCLUDED_IDS = [2, 3, 7, 8, 9, 11, 12, 13, 14, 15, 16];
  const hilosIncluded = HILOS_INCLUDED_IDS.includes(product.product_id);
  const showHilos = showTraditions;
  const THREAD_DEFAULTS = { avodat_yad_grueso: 'Gruesos', avodat_yad_delgado: 'Delgados', mechona_delgado: 'A Máquina', sin_hilos: 'Sin Hilos' };
  // Opciones de hilos REALES del producto (pricing rules) — no inventar opciones que la tienda no muestra
  const threadKeys = hilosIncluded
    ? ['incluidos']
    : (product.pricing_rules
      ? [...new Set([...(product.pricing_rules.adult_threads || []), ...(product.pricing_rules.kids_threads || [])])]
      : ['avodat_yad_grueso', 'avodat_yad_delgado', 'sin_hilos']);
  const [selGroups, setSelGroups] = useState(() => {
    const vl = product.variant_labels || {};
    const vol = product.variant_option_labels || {};
    const groups = [];
    if (showTraditions) groups.push({
      field: 'traditions', defaultLabel: 'Tradición', label: vl.traditions || 'Tradición', editableLabels: true,
      oosField: 'out_of_stock_traditions', hiddenField: 'hidden_traditions',
      options: [['sefaradi', 'Sefaradí'], ['ashkenazi', 'Ashkenazí'], ['no_amarrados', 'No Amarrados']]
        .map(([k, l]) => ({ key: k, defaultLabel: l, label: vol.traditions?.[k] || l, state: optState(k, 'out_of_stock_traditions', 'hidden_traditions') })),
    });
    if (showHilos) groups.push({
      field: 'threads', defaultLabel: 'Hilos', label: vl.threads || 'Hilos', editableLabels: true,
      oosField: 'out_of_stock_threads', hiddenField: 'hidden_threads',
      options: threadKeys.map(k => {
        const dl = k === 'incluidos' ? (product.product_id === 16 ? 'Negros' : 'Incluidos') : (THREAD_DEFAULTS[k] || k);
        return { key: k, defaultLabel: dl, label: vol.threads?.[k] || dl, state: optState(k, 'out_of_stock_threads', 'hidden_threads') };
      }),
    });
    if ((product.colors || []).length > 1) groups.push({
      field: 'colors', defaultLabel: 'Color', label: vl.colors || 'Color',
      oosField: 'out_of_stock_colors', hiddenField: 'hidden_colors',
      options: (product.colors || []).map(c => ({ key: c.value, label: c.name?.es || c.value, dot: c.value, state: optState(c.value, 'out_of_stock_colors', 'hidden_colors') })),
    });
    if (product.has_cotton_type) groups.push({
      field: 'cotton', defaultLabel: 'Tipo de algodón', label: vl.cotton || 'Tipo de algodón',
      oosField: 'out_of_stock_cotton_types', hiddenField: 'hidden_cotton_types',
      options: [['cotton_smooth', 'Liso'], ['cotton_edge', 'Acanalado']]
        .map(([k, l]) => ({ key: k, label: l, state: optState(k, 'out_of_stock_cotton_types', 'hidden_cotton_types') })),
    });
    return groups;
  });
  const [customSels, setCustomSels] = useState(() => JSON.parse(JSON.stringify(product.custom_selections || [])));
  const [removedSels, setRemovedSels] = useState(product.removed_selections || []);
  const [badges, setBadges] = useState(product.badges || []);
  const [newSelName, setNewSelName] = useState('');
  const [newSelOpts, setNewSelOpts] = useState('');

  const setGroupOptState = (gi, oi, state) =>
    setSelGroups(prev => prev.map((g, i) => i !== gi ? g : { ...g, options: g.options.map((o, j) => j === oi ? { ...o, state } : o) }));
  const setGroupAll = (gi, state) =>
    setSelGroups(prev => prev.map((g, i) => i !== gi ? g : { ...g, options: g.options.map(o => ({ ...o, state })) }));
  const setGroupLabel = (gi, label) =>
    setSelGroups(prev => prev.map((g, i) => i === gi ? { ...g, label } : g));
  const setGroupOptLabel = (gi, oi, label) =>
    setSelGroups(prev => prev.map((g, i) => i !== gi ? g : { ...g, options: g.options.map((o, j) => j === oi ? { ...o, label } : o) }));

  const deleteGroup = (gi) => {
    const g = selGroups[gi];
    if (!window.confirm(`¿Eliminar la selección "${g.label}" por completo? Desaparecerá de la tienda. Podrás restaurarla desde este mismo panel.`)) return;
    setRemovedSels(prev => prev.includes(g.field) ? prev : [...prev, g.field]);
  };
  const restoreGroup = (field) => setRemovedSels(prev => prev.filter(f => f !== field));

  const addCustomSel = () => {
    const name = newSelName.trim();
    const opts = newSelOpts.split(',').map(s => s.trim()).filter(Boolean);
    if (!name || opts.length === 0) { alert('Escribe el nombre de la selección y sus opciones separadas por coma'); return; }
    setCustomSels(prev => [...prev, {
      id: `sel_${Date.now()}`, name, state: 'visible',
      options: opts.map((label, i) => ({ key: `opt_${i}_${Date.now()}`, label, state: 'visible' })),
    }]);
    setNewSelName(''); setNewSelOpts('');
  };

  const addSize = () => {
    const s = sizeInput.trim();
    if (!s || sizes.includes(s)) { setSizeInput(''); return; }
    setSizes(prev => [...prev, s]);
    setSizeInput('');
  };
  const removeSize = (s) => setSizes(prev => prev.filter(x => x !== s));
  const moveSize = (from, to) => setSizes(prev => {
    if (to < 0 || to >= prev.length || from === to) return prev;
    const arr = [...prev];
    const [x] = arr.splice(from, 1);
    arr.splice(to, 0, x);
    return arr;
  });
  const sizeDragRef = useRef(null);

  const [pickerFor, setPickerFor] = useState(null);
  const [libFiles, setLibFiles] = useState(null);
  const openPicker = async (ci) => {
    setPickerFor(ci);
    if (libFiles === null) {
      try {
        const token = sessionStorage.getItem(ADMIN_TOKEN_KEY) || '';
        const res = await fetch(`${API_URL}/api/admin/uploads/list?limit=250`, { headers: { 'X-Admin-Token': token } });
        const data = await res.json();
        setLibFiles(data.files || []);
      } catch { setLibFiles([]); }
    }
  };
  const pickFromLibrary = (url) => {
    setColors(prev => prev.map((c, i) =>
      i === pickerFor && !c.existing.includes(url) ? { ...c, existing: [...c.existing, url] } : c));
  };
  const deleteFromLibrary = async (f) => {
    if (!window.confirm(`¿Eliminar esta foto de la biblioteca?\n${f.original_filename || ''}`)) return;
    try {
      const token = sessionStorage.getItem(ADMIN_TOKEN_KEY) || '';
      const res = await fetch(`${API_URL}/api/admin/uploads/${f.file_id}`, {
        method: 'DELETE', headers: { 'X-Admin-Token': token },
      });
      if (res.ok) {
        setLibFiles(prev => prev.filter(x => x.file_id !== f.file_id));
      } else {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || 'No se pudo eliminar la foto');
      }
    } catch { alert('No se pudo conectar al servidor'); }
  };

  const addColor = () => setColors(prev => [...prev, { uid: `col-new-${Date.now()}`, original: {}, name: '', hex: '#000000', existing: [], files: [] }]);
  const removeColor = (idx) => setColors(prev => prev.filter((_, i) => i !== idx));
  const removeExistingImg = (ci, ii) => setColors(prev => prev.map((c, i) => i === ci ? { ...c, existing: c.existing.filter((_, k) => k !== ii) } : c));
  const dragRef = useRef(null);
  const moveExisting = (ci, from, to) => {
    setColors(prev => prev.map((c, i) => {
      if (i !== ci || to < 0 || to >= c.existing.length || from === to) return c;
      const arr = [...c.existing];
      const [img] = arr.splice(from, 1);
      arr.splice(to, 0, img);
      return { ...c, existing: arr };
    }));
  };
  const removeNewFile = (ci, fi) => setColors(prev => prev.map((c, i) => i === ci ? { ...c, files: c.files.filter((_, k) => k !== fi) } : c));
  const addFiles = (ci, files) => {
    const arr = Array.from(files);
    const target = colors[ci];
    const isBrandNewColor = target && target.existing.length === 0 && target.files.length === 0;
    setColors(prev => prev.map((c, i) => i === ci ? { ...c, files: [...c.files, ...arr].slice(0, 6) } : c));
    // Auto-detect dominant color for colors added without photos yet
    if (arr[0] && isBrandNewColor && !target.userSetHex) {
      detectDominantColor(arr[0]).then(hex => {
        if (hex) setColors(prev => prev.map((c, i) => i === ci && !c.userSetHex ? { ...c, hex } : c));
      });
    }
  };

  const toEN = (s) => s.replace(/^Niño /, 'Boys ').replace(/^Adulto /, 'Adult ');

  const buildPayloadColors = async () => {
    const payloadColors = [];
    for (const c of colors) {
      const urls = [...c.existing];
      for (let i = 0; i < c.files.length; i++) {
        const kind = urls.length === 0 && i === 0 ? 'cover' : 'gallery';
        const url = await uploadProductImage(c.files[i], kind);
        urls.push(url);
      }
      const nameChanged = c.name !== (c.original.name?.es || '');
      payloadColors.push({
        ...c.original,
        name: nameChanged || !c.original.name ? { es: c.name || 'Color', en: c.name || 'Color' } : c.original.name,
        value: c.hex,
        image: urls[0],
        gallery: urls.slice(1),
      });
    }
    return payloadColors;
  };

  const buildSelectionFields = (body) => {
    // Selecciones: estados por opción → out_of_stock_* / hidden_* + labels + custom
    selGroups.forEach(g => {
      if (removedSels.includes(g.field)) return;
      body[g.oosField] = g.options.filter(o => o.state === 'oos').map(o => o.key);
      body[g.hiddenField] = g.options.filter(o => o.state === 'hidden').map(o => o.key);
    });
    body.removed_selections = removedSels;
    body.variant_labels = Object.fromEntries(
      selGroups.filter(g => g.label.trim() && g.label.trim() !== g.defaultLabel).map(g => [g.field, g.label.trim()])
    );
    const optLabels = {};
    selGroups.filter(g => g.editableLabels).forEach(g => {
      const m = Object.fromEntries(
        g.options.filter(o => o.label.trim() && o.label.trim() !== o.defaultLabel).map(o => [o.key, o.label.trim()])
      );
      if (Object.keys(m).length > 0) optLabels[g.field] = m;
    });
    body.variant_option_labels = optLabels;
    body.custom_selections = customSels;
    body.badges = badges.map(b => b.trim()).filter(Boolean);
  };

  const save = async () => {
    if (!form.name_es.trim()) { alert('Falta el nombre del producto'); return; }
    if (!form.price_mxn || parseFloat(form.price_mxn) <= 0) { alert('Falta el precio'); return; }
    if (colors.length === 0) { alert('Debe haber al menos un color'); return; }
    if (colors.some(c => c.existing.length + c.files.length === 0)) { alert('Cada color necesita al menos una foto'); return; }
    setSaving(true);
    try {
      const payloadColors = await buildPayloadColors();
      const body = {
        name: { es: form.name_es, en: form.name_en.trim() || form.name_es },
        description: { es: form.desc_es, en: form.desc_en.trim() || form.desc_es },
        material: form.material,
        style: form.style,
        price_mxn: parseFloat(form.price_mxn),
        kids_price_mxn: form.kids_price_mxn === '' || form.kids_price_mxn === null ? null : parseFloat(form.kids_price_mxn),
        colors: payloadColors,
        image: payloadColors[0].image,
        is_new: form.is_new,
        no_tzitzit: form.no_tzitzit,
      };
      const sizesChanged = JSON.stringify(sizes) !== JSON.stringify(product.sizes?.es || []);
      if (sizesChanged) {
        body.sizes = { es: sizes, en: sizes.map(toEN) };
        const oldStock = product.stock_qty || {};
        if (Object.keys(oldStock).length > 0) {
          body.stock_qty = Object.fromEntries(sizes.map(s => [s, parseInt(oldStock[s], 10) || 0]));
        }
      }
      if (form.material !== product.material) body.fabric = FABRIC_MAP[form.material] || { es: form.material, en: form.material };
      buildSelectionFields(body);
      const token = sessionStorage.getItem(ADMIN_TOKEN_KEY) || '';
      const res = await fetch(`${API_URL}/api/admin/products/${product.product_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Token': token },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onSaved();
    } catch (e) {
      alert(`Error al guardar cambios: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500";

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" onClick={onClose} data-testid="edit-product-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <h3 className="font-bold text-slate-800">✏️ Editar producto #{product.product_id}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-edit-product">✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre (Español) *</label>
              <input type="text" value={form.name_es} onChange={(e) => setForm(f => ({ ...f, name_es: e.target.value }))}
                className={inputCls} data-testid="edit-product-name-es" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre (English)</label>
              <input type="text" value={form.name_en} onChange={(e) => setForm(f => ({ ...f, name_en: e.target.value }))}
                className={inputCls} data-testid="edit-product-name-en" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Material</label>
              <select value={form.material} onChange={(e) => setForm(f => ({ ...f, material: e.target.value }))}
                className={inputCls} data-testid="edit-product-material">
                {PRODUCT_MATERIALS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Estilo</label>
              <select value={form.style} onChange={(e) => setForm(f => ({ ...f, style: e.target.value }))}
                className={inputCls} data-testid="edit-product-style">
                {styleOpts.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Precio Adulto (MXN) *</label>
              <input type="number" value={form.price_mxn} onChange={(e) => setForm(f => ({ ...f, price_mxn: e.target.value }))}
                className={inputCls} data-testid="edit-product-price" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Precio Niño (opcional)</label>
              <input type="number" value={form.kids_price_mxn} onChange={(e) => setForm(f => ({ ...f, kids_price_mxn: e.target.value }))}
                placeholder="—" className={inputCls} data-testid="edit-product-kids-price" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción (Español)</label>
              <textarea value={form.desc_es} onChange={(e) => setForm(f => ({ ...f, desc_es: e.target.value }))}
                rows={2} className={`${inputCls} resize-none`} data-testid="edit-product-desc-es" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Descripción (English)</label>
              <textarea value={form.desc_en} onChange={(e) => setForm(f => ({ ...f, desc_en: e.target.value }))}
                rows={2} className={`${inputCls} resize-none`} data-testid="edit-product-desc-en" />
            </div>
          </div>

          <SizeChipsEditor
            sizes={sizes} sizeInput={sizeInput} setSizeInput={setSizeInput}
            addSize={addSize} removeSize={removeSize} moveSize={moveSize} sizeDragRef={sizeDragRef}
          />

          <ColorPhotosEditor
            colors={colors} setColors={setColors} addColor={addColor} removeColor={removeColor}
            removeExistingImg={removeExistingImg} moveExisting={moveExisting}
            removeNewFile={removeNewFile} addFiles={addFiles} openPicker={openPicker} dragRef={dragRef}
          />

          <SelectionsEditor
            selGroups={selGroups}
            removedFields={removedSels}
            onDeleteGroup={deleteGroup}
            onRestoreGroup={restoreGroup}
            setGroupLabel={setGroupLabel} setGroupAll={setGroupAll}
            setGroupOptState={setGroupOptState} setGroupOptLabel={setGroupOptLabel}
            customSels={customSels} setCustomSels={setCustomSels}
            newSelName={newSelName} setNewSelName={setNewSelName}
            newSelOpts={newSelOpts} setNewSelOpts={setNewSelOpts}
            addCustomSel={addCustomSel}
          />

          <BadgesEditor badges={badges} setBadges={setBadges} />

          {product.pricing_rules && (
            <PricingRulesEditor product={product} isSaving={false}
              onPatch={async (pid, payload) => {
                const token = sessionStorage.getItem(ADMIN_TOKEN_KEY) || '';
                await fetch(`${API_URL}/api/products/${pid}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json', 'X-Admin-Token': token },
                  body: JSON.stringify(payload),
                });
              }} />
          )}

          <div className="flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
              <input type="checkbox" checked={form.is_new} onChange={(e) => setForm(f => ({ ...f, is_new: e.target.checked }))} data-testid="edit-product-is-new" />
              Badge &quot;NEW&quot;
            </label>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
              <input type="checkbox" checked={form.no_tzitzit} onChange={(e) => setForm(f => ({ ...f, no_tzitzit: e.target.checked }))} data-testid="edit-product-no-tzitzit" />
              SIN tzitzit (playera sencilla)
            </label>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800" data-testid="cancel-edit-product">Cancelar</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-2 bg-cyan-600 text-white text-sm font-semibold rounded-lg hover:bg-cyan-700 disabled:opacity-60"
            data-testid="submit-edit-product">
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
      {pickerFor !== null && (
        <PhotoLibraryModal
          libFiles={libFiles}
          colors={colors}
          pickerFor={pickerFor}
          onPick={pickFromLibrary}
          onDelete={deleteFromLibrary}
          onClose={() => setPickerFor(null)}
        />
      )}
    </div>
  );
};
